import type { ColumnValidator, CrossRule } from './types';

export function required(message: string): ColumnValidator {
  return (value) => {
    const str = String(value ?? '').trim();
    return str ? null : message;
  };
}

export function matchesAny(values: string[], message: string): ColumnValidator {
  const lookup = new Set(values);
  return (value) => {
    const str = String(value ?? '');
    if (!str) return null;
    return lookup.has(str) ? null : message;
  };
}

export function matchesTiered(
  all: Set<string>,
  notFoundMessage: string,
  allowed: Set<string>,
  notAllowedMessage: string,
): ColumnValidator {
  return (value) => {
    const str = String(value ?? '').trim();
    if (!str) return null;
    if (!all.has(str)) return notFoundMessage;
    if (!allowed.has(str)) return notAllowedMessage;
    return null;
  };
}

type ForbiddenPairsParams = {
  columnA: string;
  resolverA: Map<string, unknown>;
  columnB: string;
  resolverB: Map<string, unknown>;
  pairs: [unknown, unknown, string][];
};

export function forbiddenPairs({
  columnA,
  resolverA,
  columnB,
  resolverB,
  pairs,
}: ForbiddenPairsParams): CrossRule {
  return (row) => {
    const a = resolverA.get(String(row[columnA]));
    const b = resolverB.get(String(row[columnB]));
    if (a == null || b == null) return null;
    for (const [valueA, valueB, message] of pairs) {
      if (a === valueA && b === valueB) return { column: columnB, message, type: 'error' };
    }
    return null;
  };
}

export function noDuplicates(columns: string[], message: string): CrossRule {
  const seen = new Set<string>();
  return (row) => {
    const parts = columns.map((c) => String(row[c] ?? '').trim());
    const hasEmptyValue = parts.some((p) => !p);

    if (hasEmptyValue) return null;
    const key = parts.join('|');

    if (seen.has(key)) return { column: columns[0], message, type: 'error' };
    seen.add(key);
    return null;
  };
}
